import { useState, useMemo, useEffect, useCallback } from 'react';
import useAppStore from '../../store/app.store';
import api from '../../services/api';
import { IcInstagram, IcFacebook, IcTiktok, IcYoutube, IcLinkedIn, IcTwitter, IcMapPin, IcTarget, IcMegaphone, IcGlobe, IcStar, IcMail, IcPhone, IcKey, IcEye, IcEyeOff, IcCopy, IcBuilding, IcX, IcLock, IcAlertTriangle, IcLink } from '../../components/ui/Icons';
import PageHeader from '../../components/ui/PageHeader';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import toast from 'react-hot-toast';
import ContratosPage from '../contratos/Contratos';

const PORTES = ['micro', 'pequena', 'media', 'grande'];
const SEGMENTOS = ['Alimentação', 'Saúde', 'Automotivo', 'Beleza & Estética', 'Construção Civil', 'Jurídico', 'Educação', 'Fitness', 'Imóveis', 'Moda', 'Pet', 'Tecnologia', 'Varejo', 'Outro'];

const PLATAFORMA_ICON = {
  'Instagram': <IcInstagram size={13} />,
  'Facebook': <IcFacebook size={13} />,
  'TikTok': <IcTiktok size={13} />,
  'YouTube': <IcYoutube size={13} />,
  'LinkedIn': <IcLinkedIn size={13} />,
  'Twitter / X': <IcTwitter size={13} />,
  'Google Meu Negócio': <IcMapPin size={13} />,
  'Google Ads': <IcTarget size={13} />,
  'Meta Ads': <IcMegaphone size={13} />,
  'Site (painel)': <IcGlobe size={13} />,
  'Reclame Aqui': <IcStar size={13} />,
  'E-mail marketing': <IcMail size={13} />,
  'Outro': <IcKey size={13} />,
};

const PLATAFORMAS_SUGERIDAS = [
  { label: 'Instagram', icone: 'instagram' },
  { label: 'Facebook', icone: 'facebook' },
  { label: 'TikTok', icone: 'tiktok' },
  { label: 'YouTube', icone: 'youtube' },
  { label: 'LinkedIn', icone: 'linkedin' },
  { label: 'Twitter / X', icone: 'twitter' },
  { label: 'Google Meu Negócio', icone: 'mappin' },
  { label: 'Google Ads', icone: 'target' },
  { label: 'Meta Ads', icone: 'megaphone' },
  { label: 'Site (painel)', icone: 'globe' },
  { label: 'Reclame Aqui', icone: 'star' },
  { label: 'E-mail marketing', icone: 'mail' },
  { label: 'Outro', icone: 'key' },
];

// ─── Gerenciador de acessos/senhas ─────────────────────────────────────────
function GerenciadorAcessos({ acessos = [], onChange }) {
  const [mostrarSenhas, setMostrarSenhas] = useState({});
  const [novaPlat, setNovaPlat] = useState('');
  const [sugestaoAberta, setSugestaoAberta] = useState(false);

  const adicionarAcesso = (plataforma) => {
    const label = plataforma || novaPlat.trim();
    if (!label) return;
    const icone = PLATAFORMAS_SUGERIDAS.find(p => p.label === label)?.icone || 'key';
    onChange([...acessos, { id: Date.now(), plataforma: label, icone, usuario: '', senha: '', url: '', observacao: '' }]);
    setNovaPlat('');
    setSugestaoAberta(false);
  };

  const atualizarAcesso = (id, campo, valor) => {
    onChange(acessos.map(a => a.id === id ? { ...a, [campo]: valor } : a));
  };

  const removerAcesso = (id) => {
    onChange(acessos.filter(a => a.id !== id));
  };

  const toggleSenha = (id) => {
    setMostrarSenhas(p => ({ ...p, [id]: !p[id] }));
  };

  const copiar = (texto, label) => {
    navigator.clipboard.writeText(texto);
    toast.success(`${label} copiado!`);
  };

  return (
    <div className="space-y-3">
      {/* Lista de acessos */}
      {acessos.map(a => (
        <div key={a.id} className="border border-gray-200 rounded-xl overflow-hidden">
          {/* Header do acesso */}
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
            <span className="text-gray-500">{PLATAFORMA_ICON[a.plataforma] || <IcKey size={13} />}</span>
            <span className="text-sm font-semibold text-gray-700 flex-1">{a.plataforma}</span>
            <button type="button" onClick={() => removerAcesso(a.id)}
              className="text-xs text-red-400 hover:text-red-600 px-2 py-0.5 rounded hover:bg-red-50">
              Remover
            </button>
          </div>

          {/* Campos */}
          <div className="p-3 space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1 block">Usuário / E-mail</label>
                <div className="flex gap-1">
                  <input
                    className="form-input flex-1 text-xs py-1.5"
                    value={a.usuario}
                    onChange={e => atualizarAcesso(a.id, 'usuario', e.target.value)}
                    placeholder="usuario@email.com ou @usuario"
                  />
                  {a.usuario && (
                    <button type="button" onClick={() => copiar(a.usuario, 'Usuário')}
                      className="px-2 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg border border-gray-200 transition-colors" title="Copiar usuário">
                      <IcCopy size={13} />
                    </button>
                  )}
                </div>
              </div>
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1 block">Senha</label>
                <div className="flex gap-1">
                  <input
                    className="form-input flex-1 text-xs py-1.5 font-mono"
                    type={mostrarSenhas[a.id] ? 'text' : 'password'}
                    value={a.senha}
                    onChange={e => atualizarAcesso(a.id, 'senha', e.target.value)}
                    placeholder="••••••••"
                  />
                  <button type="button" onClick={() => toggleSenha(a.id)}
                    className="px-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg border border-gray-200 transition-colors" title={mostrarSenhas[a.id] ? 'Ocultar' : 'Mostrar'}>
                    {mostrarSenhas[a.id] ? <IcEyeOff size={13} /> : <IcEye size={13} />}
                  </button>
                  {a.senha && (
                    <button type="button" onClick={() => copiar(a.senha, 'Senha')}
                      className="px-2 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg border border-gray-200 transition-colors" title="Copiar senha">
                      <IcCopy size={13} />
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1 block">URL / Link de acesso (opcional)</label>
              <input
                className="form-input text-xs py-1.5"
                value={a.url}
                onChange={e => atualizarAcesso(a.id, 'url', e.target.value)}
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1 block">Observação (opcional)</label>
              <input
                className="form-input text-xs py-1.5"
                value={a.observacao}
                onChange={e => atualizarAcesso(a.id, 'observacao', e.target.value)}
                placeholder="Ex: Conta principal, 2FA ativado, conta do gestor..."
              />
            </div>
          </div>
        </div>
      ))}

      {/* Adicionar novo acesso */}
      <div className="border-2 border-dashed border-gray-200 rounded-xl p-3">
        <p className="text-xs font-semibold text-gray-500 mb-2">+ Adicionar acesso</p>

        {/* Sugestões rápidas */}
        <div className="flex flex-wrap gap-1.5 mb-2">
          {PLATAFORMAS_SUGERIDAS.filter(p => !acessos.find(a => a.plataforma === p.label)).map(p => (
            <button type="button" key={p.label} onClick={() => adicionarAcesso(p.label)}
              className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border border-gray-200 text-gray-600 hover:border-brand-400 hover:text-brand-700 hover:bg-brand-50 transition-all">
              <span className="text-gray-500">{PLATAFORMA_ICON[p.label] || <IcKey size={12} />}</span> {p.label}
            </button>
          ))}
        </div>

        {/* Campo manual */}
        <div className="flex gap-2">
          <input
            className="form-input flex-1 text-xs"
            value={novaPlat}
            onChange={e => setNovaPlat(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); adicionarAcesso(); } }}
            placeholder="Ou digite o nome da plataforma..."
          />
          <button type="button" onClick={() => adicionarAcesso()}
            className="btn-secondary text-xs px-3">+ Add</button>
        </div>
      </div>
    </div>
  );
}

// ─── Formulário do Cliente ─────────────────────────────────────────────────
function FormCliente({ inicial, onSalvar, onFechar, salvando = false }) {
  const [form, setForm] = useState({
    razao_social: '', nome_fantasia: '', cpf_cnpj: '', email: '', telefone: '',
    whatsapp: '', segmento: '', porte: 'pequena', site: '', instagram: '',
    facebook: '', tiktok: '', linkedin: '', youtube: '',
    google_meu_negocio: '', status: 'ativo', acessos: [],
    ...(inicial || {}),
  });
  const [abaForm, setAbaForm] = useState(0);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <form onSubmit={e => { e.preventDefault(); onSalvar(form); }} className="space-y-4">
      {/* Abas */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
        {['Dados Gerais', 'Redes Sociais', 'Acessos & Senhas'].map((a, i) => (
          <button type="button" key={a} onClick={() => setAbaForm(i)}
            className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-all ${abaForm === i ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}>
            {a}
          </button>
        ))}
      </div>

      {/* ABA DADOS GERAIS */}
      {abaForm === 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="col-span-1 sm:col-span-2"><label className="form-label">Razão Social *</label><input className="form-input" value={form.razao_social} onChange={e => set('razao_social', e.target.value)} required /></div>
          <div><label className="form-label">Nome Fantasia</label><input className="form-input" value={form.nome_fantasia} onChange={e => set('nome_fantasia', e.target.value)} /></div>
          <div><label className="form-label">CPF / CNPJ</label><input className="form-input" value={form.cpf_cnpj} onChange={e => set('cpf_cnpj', e.target.value)} /></div>
          <div><label className="form-label">E-mail</label><input type="email" className="form-input" value={form.email} onChange={e => set('email', e.target.value)} /></div>
          <div><label className="form-label">Telefone</label><input className="form-input" value={form.telefone} onChange={e => set('telefone', e.target.value)} /></div>
          <div><label className="form-label">WhatsApp</label><input className="form-input" value={form.whatsapp} onChange={e => set('whatsapp', e.target.value)} /></div>
          <div><label className="form-label">Site</label><input className="form-input" value={form.site} onChange={e => set('site', e.target.value)} placeholder="https://..." /></div>
          <div>
            <label className="form-label">Segmento</label>
            <select className="form-input" value={form.segmento} onChange={e => set('segmento', e.target.value)}>
              <option value="">Selecione...</option>
              {SEGMENTOS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Porte</label>
            <select className="form-input" value={form.porte} onChange={e => set('porte', e.target.value)}>
              {PORTES.map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Status</label>
            <select className="form-input" value={form.status} onChange={e => set('status', e.target.value)}>
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo</option>
              <option value="prospecto">Prospecto</option>
            </select>
          </div>
        </div>
      )}

      {/* ABA REDES SOCIAIS */}
      {abaForm === 1 && (
        <div className="space-y-3">
          <p className="text-xs text-gray-400">Perfis/páginas das redes sociais do cliente (apenas os nomes/handles).</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { k: 'instagram', label: 'Instagram', ph: '@perfil' },
              { k: 'facebook', label: '📘 Facebook', ph: 'facebook.com/pagina' },
              { k: 'tiktok', label: '🎵 TikTok', ph: '@perfil' },
              { k: 'linkedin', label: 'LinkedIn', ph: 'linkedin.com/company/...' },
              { k: 'youtube', label: 'YouTube', ph: '@canal' },
              { k: 'google_meu_negocio', label: 'Google Meu Negócio', ph: 'Nome do perfil' },
            ].map(({ k, label, ph }) => (
              <div key={k}>
                <label className="form-label">{label}</label>
                <input className="form-input" value={form[k] || ''} onChange={e => set(k, e.target.value)} placeholder={ph} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ABA ACESSOS & SENHAS */}
      {abaForm === 2 && (
        <div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
            <p className="text-xs font-semibold text-amber-800 flex items-center gap-1"><IcAlertTriangle size={12} /> Informação confidencial</p>
            <p className="text-xs text-amber-700 mt-0.5">Estas informações são sigilosas. Apenas membros da equipe Legacy com acesso ao sistema conseguem visualizá-las.</p>
          </div>
          <GerenciadorAcessos
            acessos={form.acessos || []}
            onChange={val => set('acessos', val)} />
        </div>
      )}

      <div className="flex gap-2 pt-2 border-t border-gray-100">
        <button type="submit" disabled={salvando} className="btn-primary flex-1">{salvando ? 'Salvando...' : (inicial ? 'Salvar alterações' : 'Criar Cliente')}</button>
        <button type="button" onClick={onFechar} className="btn-secondary">Cancelar</button>
      </div>
    </form>
  );
}

// ─── Página principal ──────────────────────────────────────────────────────
export default function Clientes() {
  const contratos = useAppStore(s => s.contratos);

  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [aba, setAba] = useState('clientes');
  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroSegmento, setFiltroSegmento] = useState('');
  const [modalNovo, setModalNovo] = useState(false);
  const [editando, setEditando] = useState(null);
  const [detalhe, setDetalhe] = useState(null);
  const [viewMode, setViewMode] = useState('cards');

  const carregarClientes = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/clientes', { params: { limit: 200 } });
      setClientes(data.data || []);
    } catch {
      toast.error('Erro ao carregar clientes.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregarClientes(); }, [carregarClientes]);

  const addCliente = async (form) => {
    setSalvando(true);
    try {
      await api.post('/clientes', form);
      toast.success('Cliente cadastrado!');
      setModalNovo(false);
      carregarClientes();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao cadastrar cliente.');
    } finally {
      setSalvando(false);
    }
  };

  const updateCliente = async (id, form) => {
    setSalvando(true);
    try {
      await api.patch(`/clientes/${id}`, form);
      toast.success('Cliente atualizado!');
      setEditando(null);
      carregarClientes();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erro ao atualizar cliente.');
    } finally {
      setSalvando(false);
    }
  };

  const filtrados = useMemo(() => clientes.filter(c => {
    const match = !busca || [c.razao_social, c.nome_fantasia, c.cpf_cnpj, c.email].some(v => v?.toLowerCase().includes(busca.toLowerCase()));
    return match && (!filtroStatus || c.status === filtroStatus) && (!filtroSegmento || c.segmento === filtroSegmento);
  }), [clientes, busca, filtroStatus, filtroSegmento]);

  const getContratosCliente = (id) => contratos.filter(c => c.cliente_id === id && c.status === 'ativo');

  return (
    <div>
      <PageHeader
        title="Clientes"
        subtitle={loading ? 'Carregando...' : `${clientes.filter(c => c.status === 'ativo').length} clientes ativos`}
        actions={aba === 'clientes' && <button onClick={() => setModalNovo(true)} className="btn-primary">+ Novo Cliente</button>}
      />

      {/* ── Abas ── */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '1px solid #2A3652' }}>
        {[{ id: 'clientes', label: 'Clientes' }, { id: 'contratos', label: 'Contratos' }].map(t => (
          <button
            key={t.id}
            onClick={() => setAba(t.id)}
            style={{
              padding: '8px 20px',
              fontSize: 14,
              fontWeight: aba === t.id ? 600 : 400,
              color: aba === t.id ? '#6C4CF5' : '#94A3B8',
              background: 'none',
              border: 'none',
              borderBottom: aba === t.id ? '2px solid #6C4CF5' : '2px solid transparent',
              cursor: 'pointer',
              transition: 'all .15s',
              marginBottom: -1,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {aba === 'contratos' && <ContratosPage embedded />}
      {aba !== 'clientes' && null}
      {aba === 'clientes' && <>


      <div className="flex gap-3 mb-5 flex-wrap">
        <input className="form-input flex-1 min-w-0 w-full sm:w-auto" placeholder="Buscar por nome, CNPJ, e-mail..."
          value={busca} onChange={e => setBusca(e.target.value)} />
        <select className="form-input w-auto" value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)}>
          <option value="">Todos status</option>
          <option value="ativo">Ativo</option>
          <option value="inativo">Inativo</option>
          <option value="prospecto">Prospecto</option>
        </select>
        <select className="form-input w-auto" value={filtroSegmento} onChange={e => setFiltroSegmento(e.target.value)}>
          <option value="">Todos segmentos</option>
          {SEGMENTOS.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <div className="flex rounded-lg border border-gray-200 overflow-hidden">
          <button onClick={() => setViewMode('cards')} title="Cards"
            className={`px-3 py-2 text-sm transition-colors ${viewMode === 'cards' ? 'bg-brand-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
              <path d="M1 2a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1zm5 0a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1zm5 0a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1zM1 7a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1zm5 0a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1zm5 0a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1zM1 12a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1zm5 0a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1zm5 0a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1z"/>
            </svg>
          </button>
          <button onClick={() => setViewMode('lista')} title="Lista"
            className={`px-3 py-2 text-sm transition-colors ${viewMode === 'lista' ? 'bg-brand-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
              <path fillRule="evenodd" d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5"/>
            </svg>
          </button>
        </div>
      </div>

      {/* ── Vista Lista ── */}
      {viewMode === 'lista' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-4">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Cliente</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Segmento</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">E-mail</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">WhatsApp</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Contrato</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtrados.map(c => {
                const cts = getContratosCliente(c.id);
                return (
                  <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-gray-900">{c.nome_fantasia || c.razao_social}</p>
                      <p className="text-xs text-gray-400">{c.razao_social}</p>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{c.segmento || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{c.email || '—'}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{c.whatsapp || c.telefone || '—'}</td>
                    <td className="px-4 py-3"><Badge status={c.status}>{c.status}</Badge></td>
                    <td className="px-4 py-3">
                      {cts.length > 0
                        ? <Badge variant="verde">{cts.length} contrato{cts.length > 1 ? 's' : ''}</Badge>
                        : <Badge variant="cinza">Sem contrato</Badge>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-3">
                        <button onClick={() => setDetalhe(c)} className="text-xs text-brand-600 hover:underline font-medium">Ver</button>
                        <button onClick={() => setEditando(c)} className="text-xs text-gray-500 hover:underline">Editar</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!filtrados.length && (
                <tr><td colSpan={7} className="text-center py-12 text-gray-400 text-sm">Nenhum cliente encontrado.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Vista Cards ── */}
      {viewMode === 'cards' && <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtrados.map(c => {
          const cts = getContratosCliente(c.id);
          const qtdAcessos = (c.acessos || []).length;
          const redes = [c.instagram && <IcInstagram size={14} />, c.facebook && <IcFacebook size={14} />, c.tiktok && <IcTiktok size={14} />, c.youtube && <IcYoutube size={14} />, c.linkedin && <IcLinkedIn size={14} />, c.google_meu_negocio && <IcMapPin size={14} />].filter(Boolean);
          return (
            <div key={c.id} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate">{c.nome_fantasia || c.razao_social}</h3>
                  <p className="text-xs text-gray-400 truncate">{c.razao_social}</p>
                </div>
                <Badge status={c.status}>{c.status}</Badge>
              </div>

              <div className="space-y-1 text-xs text-gray-500 mb-3">
                {c.segmento && <p>{c.segmento}{c.porte ? ` · ${c.porte}` : ''}</p>}
                {c.email && <p className="truncate flex items-center gap-1"><IcMail size={11} /> {c.email}</p>}
                {c.whatsapp && <p className="flex items-center gap-1"><IcPhone size={11} /> {c.whatsapp}</p>}
                {c.site && <p className="truncate flex items-center gap-1"><IcGlobe size={11} /> {c.site}</p>}
              </div>

              {/* Redes sociais cadastradas */}
              {redes.length > 0 && (
                <div className="flex gap-1 mb-3">
                  {redes.map((r, i) => <span key={i} className="text-gray-400">{r}</span>)}
                </div>
              )}

              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <div className="flex items-center gap-2">
                  {cts.length > 0
                    ? <Badge variant="verde">{cts.length} contrato{cts.length > 1 ? 's' : ''}</Badge>
                    : <Badge variant="cinza">Sem contrato</Badge>}
                  {qtdAcessos > 0 && (
                    <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded-full font-semibold">
                      <IcLock size={10} /> {qtdAcessos}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setDetalhe(c)} className="text-xs text-brand-600 hover:underline font-medium">Ver</button>
                  <button onClick={() => setEditando(c)} className="text-xs text-gray-500 hover:underline">Editar</button>
                </div>
              </div>
            </div>
          );
        })}
        {!filtrados.length && (
          <div className="col-span-3 text-center py-16 text-gray-400">
            <div className="flex justify-center mb-3 text-gray-300"><IcBuilding size={44} /></div>
            <p>Nenhum cliente encontrado.</p>
          </div>
        )}
      </div>}

      <Modal open={modalNovo} onClose={() => setModalNovo(false)} title="Novo Cliente" size="xl">
        <FormCliente onSalvar={addCliente} onFechar={() => setModalNovo(false)} salvando={salvando} />
      </Modal>

      <Modal open={!!editando} onClose={() => setEditando(null)} title="Editar Cliente" size="xl">
        {editando && <FormCliente inicial={editando} onSalvar={d => updateCliente(editando.id, d)} onFechar={() => setEditando(null)} salvando={salvando} />}
      </Modal>

      <Modal open={!!detalhe} onClose={() => setDetalhe(null)} title={detalhe?.razao_social} size="xl">
        {detalhe && <DetalheCliente cliente={detalhe} contratos={getContratosCliente(detalhe.id)} onEditar={() => { setEditando(detalhe); setDetalhe(null); }} />}
      </Modal>
      </>}
    </div>
  );
}

// ─── Detalhe do Cliente ─────────────────────────────────────────────────────
function DetalheCliente({ cliente: c, contratos, onEditar }) {
  const [mostrarSenhas, setMostrarSenhas] = useState({});
  const [abaDetalhe, setAbaDetalhe] = useState(0);

  const copiar = (texto, label) => { navigator.clipboard.writeText(texto); toast.success(`${label} copiado!`); };

  const redes = [
    { label: 'Instagram',          icon: <IcInstagram size={18} />, valor: c.instagram },
    { label: 'Facebook',           icon: <IcFacebook size={18} />,  valor: c.facebook },
    { label: 'TikTok',             icon: <IcTiktok size={18} />,    valor: c.tiktok },
    { label: 'LinkedIn',           icon: <IcLinkedIn size={18} />,  valor: c.linkedin },
    { label: 'YouTube',            icon: <IcYoutube size={18} />,   valor: c.youtube },
    { label: 'Google Meu Negócio', icon: <IcMapPin size={18} />,    valor: c.google_meu_negocio },
  ].filter(r => r.valor);

  return (
    <div className="space-y-4">
      {/* Sub-tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
        {['Informações', 'Redes Sociais', 'Acessos', 'Contratos'].map((a, i) => (
          <button key={a} onClick={() => setAbaDetalhe(i)}
            className={`flex-1 py-1.5 rounded-md text-xs font-medium transition-all ${abaDetalhe === i ? 'bg-white shadow text-gray-900' : 'text-gray-500'}`}>
            {a}
          </button>
        ))}
      </div>

      {/* ABA INFORMAÇÕES */}
      {abaDetalhe === 0 && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <div><span className="text-gray-400 text-xs">CNPJ/CPF</span><p className="font-medium">{c.cpf_cnpj || '—'}</p></div>
            <div><span className="text-gray-400 text-xs">Porte</span><p className="font-medium capitalize">{c.porte || '—'}</p></div>
            <div><span className="text-gray-400 text-xs">Segmento</span><p className="font-medium">{c.segmento || '—'}</p></div>
            <div><span className="text-gray-400 text-xs">Status</span><div className="mt-0.5"><Badge status={c.status}>{c.status}</Badge></div></div>
            <div><span className="text-gray-400 text-xs">E-mail</span><p className="font-medium">{c.email || '—'}</p></div>
            <div><span className="text-gray-400 text-xs">Telefone</span><p className="font-medium">{c.telefone || '—'}</p></div>
            <div><span className="text-gray-400 text-xs">WhatsApp</span><p className="font-medium">{c.whatsapp || '—'}</p></div>
            {c.site && <div className="col-span-2"><span className="text-gray-400 text-xs">Site</span><p className="font-medium text-brand-600">{c.site}</p></div>}
          </div>
          <button onClick={onEditar} className="btn-secondary w-full text-xs">Editar cliente</button>
        </div>
      )}

      {/* ABA REDES SOCIAIS */}
      {abaDetalhe === 1 && (
        <div className="space-y-2">
          {redes.length ? redes.map(r => (
            <div key={r.label} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
              <span className="text-gray-500">{r.icon}</span>
              <div className="flex-1">
                <p className="text-xs text-gray-400">{r.label}</p>
                <p className="text-sm font-medium text-gray-800">{r.valor}</p>
              </div>
              <button onClick={() => copiar(r.valor, r.label)} className="text-xs text-gray-400 hover:text-brand-600 px-2 py-1 rounded-lg hover:bg-brand-50"><IcCopy size={13} /></button>
            </div>
          )) : <p className="text-sm text-gray-400 text-center py-6">Nenhuma rede social cadastrada.</p>}
        </div>
      )}

      {/* ABA ACESSOS */}
      {abaDetalhe === 2 && (
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
            <p className="text-xs text-amber-700 flex items-center gap-1"><IcAlertTriangle size={12} /> Informações confidenciais — use com responsabilidade.</p>
          </div>
          {(c.acessos || []).length ? (c.acessos || []).map(a => (
            <div key={a.id} className="border border-gray-200 rounded-xl overflow-hidden">
              <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
                <span className="text-gray-500">{PLATAFORMA_ICON[a.plataforma] || <IcKey size={14} />}</span>
                <span className="text-sm font-semibold text-gray-700">{a.plataforma}</span>
              </div>
              <div className="p-3 space-y-2">
                {a.usuario && (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase font-semibold">Usuário</p>
                      <p className="text-sm font-mono text-gray-800">{a.usuario}</p>
                    </div>
                    <button onClick={() => copiar(a.usuario, 'Usuário')} className="text-xs text-gray-400 hover:text-brand-600 px-2 py-1 rounded-lg hover:bg-brand-50 flex items-center gap-1"><IcCopy size={11} /> Copiar</button>
                  </div>
                )}
                {a.senha && (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase font-semibold">Senha</p>
                      <p className="text-sm font-mono text-gray-800">
                        {mostrarSenhas[a.id] ? a.senha : '••••••••••••'}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => setMostrarSenhas(p => ({ ...p, [a.id]: !p[a.id] }))}
                        className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded-lg hover:bg-gray-100">
                        {mostrarSenhas[a.id] ? <><IcEyeOff size={11} /> Ocultar</> : <><IcEye size={11} /> Ver</>}
                      </button>
                      <button onClick={() => copiar(a.senha, 'Senha')} className="text-xs text-gray-400 hover:text-brand-600 px-2 py-1 rounded-lg hover:bg-brand-50 flex items-center gap-1"><IcCopy size={11} /> Copiar</button>
                    </div>
                  </div>
                )}
                {a.url && (
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase font-semibold mb-0.5">URL</p>
                    <a href={a.url} target="_blank" rel="noopener noreferrer" className="text-xs text-brand-600 hover:underline truncate block">{a.url}</a>
                  </div>
                )}
                {a.observacao && (
                  <div>
                    <p className="text-[10px] text-gray-400 uppercase font-semibold mb-0.5">Observação</p>
                    <p className="text-xs text-gray-600">{a.observacao}</p>
                  </div>
                )}
              </div>
            </div>
          )) : (
            <div className="text-center py-8 text-gray-400">
              <IcLock size={32} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
              <p className="text-sm">Nenhum acesso cadastrado.</p>
              <button onClick={onEditar} className="text-xs text-brand-600 hover:underline mt-2 block mx-auto">Clique em Editar para adicionar</button>
            </div>
          )}
        </div>
      )}

      {/* ABA CONTRATOS */}
      {abaDetalhe === 3 && (
        <div>
          {contratos.length ? contratos.map(ct => (
            <div key={ct.id} className="bg-gray-50 rounded-lg p-3 text-sm mb-2">
              <div className="flex justify-between">
                <p className="font-medium">{ct.titulo}</p>
                <Badge variant="verde">Ativo</Badge>
              </div>
              <p className="text-gray-500 text-xs mt-1">
                {Number(ct.valor_mensal).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}/mês · até {new Date(ct.data_fim).toLocaleDateString('pt-BR')}
              </p>
            </div>
          )) : <p className="text-sm text-gray-400 py-4 text-center">Sem contratos ativos.</p>}
        </div>
      )}
    </div>
  );
}
